import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Paragraph, Button, IconButton } from './ui';
import { HamburgerIcon } from './ui';
import { Drawer, DrawerHeader, DrawerBody } from './ui';
import { LanguageSelector } from './LanguageSelector';
import { useModal } from '../hooks/useModal';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { BRANDING, NAVIGATION } from '../utils/constants';
import { cn } from '../utils/cn';

const MotionDiv = motion.div;

interface NavLinkProps {
  children: React.ReactNode;
  to: string;
  isCurrent?: boolean;
  onClick?: () => void;
}

const NavLink: React.FC<NavLinkProps> = ({ children, to, isCurrent, onClick }) => (
  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        'relative px-3 py-2 font-medium tracking-wide text-neutral-50 transition-colors hover:text-neutral-100/90',
        isCurrent && 'text-neutral-50'
      )}
    >
      {children}
      {isCurrent && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-50"
          layoutId="underline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}
    </Link>
  </motion.div>
);

export default function Navbar() {
  const { t } = useTranslation('navigation');
  const { isOpen, onOpen, onClose } = useModal();
  const localizePath = useLocalizedPath();
  const exploreSectionHref = `${localizePath('/')}#${NAVIGATION.SECTIONS.FLOORS_OVERVIEW}`;
  const location = useLocation();
  const { scrollY } = useScroll();
  const headerHeight = useTransform(scrollY, [0, 100], [100, 80]);

  // Get current route for active state
  const getCurrentRoute = () => {
    const path = location.pathname;
    if (path === '/' || path.includes('/home')) return 'home';
    if (path.includes('/tour')) return 'tour';
    if (path.includes('/contact')) return 'contact';
    if (path.includes('/floors')) return 'floors';
    if (path.includes('/spaces')) return 'spaces';
    return 'home';
  };

  const currentRoute = getCurrentRoute();

  return (
    <MotionDiv 
      style={{ 
        backgroundColor: 'rgba(29, 68, 64, 0.95)',
        backdropFilter: "blur(10px)",
        height: headerHeight
      }}
      className="px-4 border-b border-neutral-200/35 fixed top-0 left-0 right-0 z-50 shadow-lg text-neutral-50"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100 }}
    >
      <div className="container mx-auto max-w-7xl h-full">
        <div className="flex h-full items-center justify-between">
          <IconButton
            size="md"
            icon={<HamburgerIcon />}
            aria-label={t('menu')}
            variant="ghost"
            className="md:hidden text-neutral-100 hover:bg-neutral-50/25 border border-neutral-50/35 backdrop-blur-sm"
            onClick={onOpen}
          />
          
          <motion.div 
            whileHover={{ scale: 1.05 }} 
            transition={{ type: "spring", stiffness: 300 }}
            className="flex items-center h-full"
          >
            <Link to={localizePath('/')} className="flex items-center">
              <img src="/Plaza/aurora.png" alt="Aurora Plaza Logo" className="h-12" />
              <Paragraph 
                className="ml-3 text-xl font-bold text-neutral-50 hidden md:block tracking-wide"
              >
                {BRANDING.APP_NAME}
              </Paragraph>
            </Link>
          </motion.div>
          
          <div className="flex items-center gap-8">
            <nav className="hidden md:flex gap-6">
              <NavLink to={localizePath('/')} isCurrent={currentRoute === 'home'}>
                {t('home')}
              </NavLink>
              <NavLink to={localizePath('/tour')} isCurrent={currentRoute === 'tour'}>
                {t('tour')}
              </NavLink>
              <NavLink to={localizePath('/floors')} isCurrent={currentRoute === 'floors'}>
                {t('floors')}
              </NavLink>
              <NavLink to={localizePath('/contact')} isCurrent={currentRoute === 'contact'}>
                {t('contact')}
              </NavLink>
            </nav>
            
            <div className="w-px h-8 bg-neutral-200/35 hidden md:block" />
            
            <LanguageSelector className="hidden md:block" />
            
            <Button
              as={Link}
              to={exploreSectionHref}
              variant="secondary"
              size="md"
              className="bg-neutral-50 text-primary-700 font-bold rounded-full px-6 hover:transform hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              {t('requestVisit')}
            </Button>
          </div>
        </div>
      </div>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerHeader 
          className="bg-primary-600 text-neutral-50"
          onClose={onClose}
        >
          <img src="/Plaza/aurora.png" alt="Aurora Plaza Logo" className="h-10 mb-2" />
          {t('menu')}
        </DrawerHeader>
        <DrawerBody className="bg-gradient-to-b from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-900">
          <div className="space-y-5 mt-6">
            <Link 
              to={localizePath('/')}
              onClick={onClose}
              className={cn(
                'block px-3 py-2 font-medium text-neutral-800 dark:text-neutral-200 hover:text-primary-600 dark:hover:text-primary-400',
                currentRoute === 'home' && 'text-primary-600 dark:text-primary-400'
              )}
            >
              {t('home')}
            </Link>
            <Link
              to={localizePath('/tour')}
              onClick={onClose}
              className={cn(
                'block px-3 py-2 font-medium text-neutral-800 dark:text-neutral-200 hover:text-primary-600 dark:hover:text-primary-400',
                currentRoute === 'tour' && 'text-primary-600 dark:text-primary-400'
              )}
            >
              {t('tour')}
            </Link>
            <Link
              to={localizePath('/floors')}
              onClick={onClose}
              className={cn(
                'block px-3 py-2 font-medium text-neutral-800 dark:text-neutral-200 hover:text-primary-600 dark:hover:text-primary-400',
                currentRoute === 'floors' && 'text-primary-600 dark:text-primary-400'
              )}
            >
              {t('floors')}
            </Link>
            <Link
              to={localizePath('/contact')}
              onClick={onClose}
              className={cn(
                'block px-3 py-2 font-medium text-neutral-800 dark:text-neutral-200 hover:text-primary-600 dark:hover:text-primary-400',
                currentRoute === 'contact' && 'text-primary-600 dark:text-primary-400'
              )}
            >
              {t('contact')}
            </Link>
            
            <div className="border-t border-neutral-200 dark:border-neutral-700 my-4" />
            
            <LanguageSelector variant="inline" className="px-3" />
            
            <div className="border-t border-neutral-200 dark:border-neutral-700 my-4" />
            
            <Button
              as={Link}
              to={exploreSectionHref}
              variant="primary"
              size="md"
              className="w-full rounded-md shadow-md hover:transform hover:-translate-y-0.5 hover:shadow-lg transition-all"
              onClick={onClose}
            >
              {t('requestVisit')}
            </Button>
          </div>
        </DrawerBody>
      </Drawer>
    </MotionDiv>
  );
}
